import { useAuth } from '@/features/auth/hooks/useAuth'
import { useProfile } from '../hooks/useProfile'
import { ProfileImageUpload } from '../components/ProfileImageUpload'
import { ProfileForm } from '../components/ProfileForm'
import { PasswordForm } from '../components/PasswordForm'
import type { User } from '@/types/entities'

export function ProfilePage() {
  const { user } = useAuth()
  const {
    updateProfile,
    isUpdatingProfile,
    updatePassword,
    isUpdatingPassword,
    uploadProfileImage,
    isUploadingImage,
  } = useProfile()

  if (!user) return null

  const handleProfileUpdate = (data: Partial<User>) => {
    updateProfile({ userId: user.id, data })
  }

  const handlePasswordUpdate = async (currentPassword: string, newPassword: string) => {
    try {
      await updatePassword({ currentPassword, newPassword })
      return true
    } catch {
      // Reported by the mutation's error toast; the form keeps its values for a retry.
      return false
    }
  }

  const handleImageUpload = (file: File) => {
    uploadProfileImage({ userId: user.id, file })
  }

  return (
    <div className="space-y-8 p-8">
      <div className="space-y-1">
        <h1 className="font-serif text-4xl font-semibold tracking-tight">My Account</h1>
        <p className="text-muted-foreground text-base">
          Manage your profile and account settings
        </p>
      </div>

      <div className="grid gap-8 lg:grid-cols-[300px_1fr]">
        {/* Profile Image Section */}
        <div>
          <ProfileImageUpload
            user={user}
            onUpload={handleImageUpload}
            isUploading={isUploadingImage}
          />
        </div>

        {/* Forms Section */}
        <div className="space-y-8">
          <ProfileForm user={user} onSubmit={handleProfileUpdate} isSubmitting={isUpdatingProfile} />
          <PasswordForm onSubmit={handlePasswordUpdate} isSubmitting={isUpdatingPassword} />
        </div>
      </div>
    </div>
  )
}

